<template>
  <DialogTemplate
    align="center"
    class="dialog"
    theme="secondary-dark"
    :show-controls="true"
    v-bind="props"
    @close="emit('close')"
  >
    <template #header>
      <div class="status">
        <Vue3Lottie
          ref="animationElement"
          class="animation"
          :animation-data="animation"
          :width="140"
        />
        <DialogStreakCount :day-count="count" />
        <span ref="titleElement" class="title">{{ title }}</span>
      </div>
    </template>
    <DialogStreakDays
      ref="streakDaysElement"
      class="streak-days"
      :count="count"
      :ready="animationStreakDaysReady"
    />
    <p ref="detailsElement" class="details">{{ details }}</p>
    <template #footer>
      <ButtonBase
        ref="buttonElement"
        rounded
        class="button"
        theme="secondary"
        variant="tonal"
        stroke="n"
        to="/gaming#streak"
        @click="() => dialog.close()"
      >
        {{ action }}
      </ButtonBase>
    </template>
  </DialogTemplate>
</template>
<script lang="ts" setup>
import { gsap } from 'gsap';

import { Vue3Lottie } from 'vue3-lottie';

import animation from './animation.json';

interface Props {
  count: number;
  title: string;
  details: string;
  action: string;
}

const props = defineProps({
  count: null,
  title: null,
  details: null,
  action: null
});

const animationStreakDaysReady = ref<boolean>(false);

const animationElement = ref(null);

const streakDaysElement = ref(null);

const detailsElement = ref(null);

const titleElement = ref(null);

const buttonElement = ref(null);

const emit = defineEmits(["close"]);

const dialog = useDialogStore();

const _startAnimationStreakDays = () => {
  const scrollEvent = new Event('scroll', {
    bubbles: true, // Allows the event to bubble up through the DOM
    cancelable: true, // Indicates whether the event can be canceled
  });

  // Dispatch the scroll event
  document?.querySelector('.streak-days')?.dispatchEvent(scrollEvent);
  requestAnimationFrame(() => {
    animationStreakDaysReady.value = true;
  });
};

onMounted(() => {
  const tl = gsap.timeline();

  tl.fromTo(
    titleElement.value,
    { alpha: 0, scale: 0.5 },
    {
      delay: 0,
      alpha: 1,
      scale: 1,
      duration: 1,
      ease: 'elastic.out(1,0.4)',
      onComplete: _startAnimationStreakDays,
    },
    1
  );
  tl.fromTo(
    streakDaysElement.value.$el,
    { alpha: 0 },
    {
      delay: 0,
      alpha: 1,
      duration: 0.5,
      ease: 'power4.out',
      onComplete: _startAnimationStreakDays,
    },
    1.2
  );
  tl.fromTo(
    detailsElement.value,
    { alpha: 0 },
    { alpha: 1, duration: 0.4, ease: 'power4.out' },
    1.4
  );
  tl.fromTo(
    buttonElement.value.$el,
    { alpha: 0, scale: 1.2 },
    { scale: 1, alpha: 1, duration: 0.4, ease: 'power4.out' },
    2
  );
});
</script>
<style lang="scss" scoped>
.dialog {
  position: relative;
  padding: var(--gap_l);
  background-color: var(--color_secondary_darker);
  gap: var(--gap_xl);
  text-align: center;
  @include above('l') {
    max-width: 48rem;
  }
}
.status {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--gap_n);
  text-align: center;
}
.count {
  @include font('xxl', 600, 'secondary');

  color: var(--color_neutral_25);
  font-size: 8rem;
}
.title {
  @include font('l', 600, 'secondary');

  transform: scale(0.8);
  opacity: 0;
  color: var(--color_neutral_25);
}

.details {
  @include font('n', 400, 'secondary');

  opacity: 0;
  color: var(--color_neutral_25);
}

:deep(.dialog-content) {
  gap: var(--gap_xl);
}

:deep(footer) {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
